"use client";

import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from "lucide-react";
import { Toaster as Sonner, type ToasterProps } from "sonner";

import { cn } from "@/lib/utils";

export function Toaster({ ...props }: ToasterProps) {
  return (
    <Sonner
      className="toaster"
      position="top-right"
      closeButton
      expand
      offset={{ top: "1rem", right: "1rem" }}
      mobileOffset={{ top: "0.75rem", right: "0.75rem" }}
      gap={12}
      richColors={false}
      theme="light"
      visibleToasts={4}
      toastOptions={{
        duration: 4500,
        classNames: {
          toast: cn(
            "group/toast !font-sans",
            "!items-start !gap-3 !p-4 !pr-10",
            "!w-full !max-w-[min(100%,22rem)] sm:!max-w-sm",
            "!rounded-2xl !border !border-zinc-200/90 !bg-white/95 !text-zinc-900",
            "shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05),0_20px_40px_-12px_rgba(0,0,0,0.15)]",
            "backdrop-blur-sm",
            "dark:!border-zinc-700/80 dark:!bg-zinc-900/95 dark:!text-zinc-50",
          ),
          title:
            "!text-sm !font-semibold !leading-snug !tracking-tight !text-inherit",
          description: cn(
            "!text-[0.8125rem] !leading-relaxed !text-zinc-600",
            "dark:!text-zinc-400",
          ),
          content: "!gap-0.5",
          closeButton: cn(
            "!absolute !right-2 !top-2",
            "!flex !h-7 !w-7 !items-center !justify-center !rounded-lg",
            "!border !border-zinc-200/80 !bg-white/90 !text-zinc-500",
            "!opacity-50 !transition-[opacity,background,transform,box-shadow] hover:!opacity-100",
            "hover:!bg-zinc-100 hover:!text-zinc-800",
            "group-hover/toast:!opacity-80 group-hover/toast:hover:!opacity-100",
            "dark:!border-zinc-600 dark:!bg-zinc-800/90 dark:!text-zinc-400 dark:hover:!bg-zinc-700",
          ),
          success: cn(
            "!border-l-[3px] !border-l-emerald-500 !pl-[calc(1rem-3px)]",
            "dark:!border-l-emerald-400",
          ),
          error: cn(
            "!border-l-[3px] !border-l-rose-500 !pl-[calc(1rem-3px)]",
            "dark:!border-l-rose-400",
          ),
          info: cn(
            "!border-l-[3px] !border-l-sky-500 !pl-[calc(1rem-3px)]",
            "dark:!border-l-sky-400",
          ),
          warning: cn(
            "!border-l-[3px] !border-l-amber-500 !pl-[calc(1rem-3px)]",
            "dark:!border-l-amber-400",
          ),
          loading: "!border-l-[3px] !border-l-zinc-400 !pl-[calc(1rem-3px)]",
        },
      }}
      icons={{
        success: <CheckCircle2 className="size-[18px] shrink-0 text-emerald-600" strokeWidth={2} />,
        error: <XCircle className="size-[18px] shrink-0 text-rose-600" strokeWidth={2} />,
        info: <Info className="size-[18px] shrink-0 text-sky-600" strokeWidth={2} />,
        warning: <AlertTriangle className="size-[18px] shrink-0 text-amber-600" strokeWidth={2} />,
        loading: <Loader2 className="size-[18px] shrink-0 animate-spin text-zinc-500" strokeWidth={2} />,
      }}
      {...props}
    />
  );
}
